import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Tag, Tooltip, Alert, Empty, Space } from 'antd';
import {
  ReloadOutlined, EyeOutlined, MobileOutlined, EnvironmentOutlined,
  AudioOutlined, ClockCircleOutlined, PaperClipOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import KpiCards from '../../components/KpiCards';
import mobileService from '../../services/mobileService';
import { formatDate, can, HAB, TYPES_DOMMAGE } from '../../variables/constants';
import DeclarationMobileDrawer from './components/mobile/DeclarationMobileDrawer';
import PageHeader from '../../components/PageHeader';

const GREEN = '#1a6b3c';
const MAGENTA = '#db2777';
const AMBER = '#d97706';
const MUTED = '#6b7280';

/**
 * File d'attente des declarations mobiles.
 *
 * Alimentee par `GET /v1/mobile-backoffice/declarations` : dossiers de canal MOBILE
 * au statut SOUMISE, la plus ancienne d'abord. Le tri vient du backend et n'est pas
 * negociable cote client -- c'est une file qu'on vide, pas un tableau qu'on explore.
 *
 * Le backend n'expose ni recherche ni filtre de date sur cette route : aucun champ
 * de filtre n'est propose ici, plutot que d'en afficher qui ne feraient rien.
 *
 * Une declaration mobile qui attend est une victime qui attend : l'age de chaque
 * ligne est mis en evidence, et au-dela de 2 jours il passe en rouge.
 */

const DOMMAGE_META = {
  CORPOREL: { color: 'red' },
  MATERIEL: { color: 'orange' },
  MIXTE: { color: 'volcano' },
};

const SEUIL_ALERTE_J = 2;

const Dash = () => <span style={{ color: '#9ca3af' }}>-</span>;

const libelleDommage = (v) => TYPES_DOMMAGE.find((o) => o.value === v)?.label || v;

/**
 * Age en JOURS et non en heures : `dateDeclaration` est un LocalDate cote backend,
 * sans heure. Afficher « 14 h » a partir d'une date nue serait une precision inventee.
 */
const ageEnJours = (date) => {
  if (!date) return null;
  const d = dayjs(date);
  return d.isValid() ? dayjs().startOf('day').diff(d.startOf('day'), 'day') : null;
};

const AgeBadge = ({ date }) => {
  const { t } = useTranslation('sinistres');
  const j = ageEnJours(date);
  if (j == null) return <Dash />;
  const texte = j <= 0 ? t('declarationsMobiles.age.aujourdhui') : j === 1 ? t('declarationsMobiles.age.hier') : t('declarationsMobiles.age.jours', { j });
  return (
    <Tag
      icon={<ClockCircleOutlined />}
      color={j >= SEUIL_ALERTE_J ? 'red' : j === 1 ? 'orange' : 'default'}
      style={{ borderRadius: 14, fontSize: 11, fontWeight: 650, marginInlineEnd: 0 }}
    >
      {texte}
    </Tag>
  );
};

/**
 * Le declarant saisi au formulaire et le titulaire du compte mobile sont DEUX
 * identites distinctes. Un ecart entre les deux est une information pour l'agent :
 * declaration faite pour le compte d'un tiers, ou tentative d'usurpation. Les
 * confondre effacerait precisement le signal qu'on veut lui montrer.
 */
const memeIdentite = (r) =>
  (r.declarantNom || '').trim().toLowerCase() === (r.usagerNom || '').trim().toLowerCase()
  && (r.declarantPrenom || '').trim().toLowerCase() === (r.usagerPrenom || '').trim().toLowerCase();

const nomComplet = (prenom, nom) => [prenom, nom].filter(Boolean).join(' ');

export default function DeclarationsMobiles() {
  const { t } = useTranslation('sinistres');
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [tiroir, setTiroir] = useState({ open: false, id: null });

  const { current, pageSize } = pagination;
  // Deux droits distincts : lire la file (accorde au REDACTEUR) et trancher.
  const peutLire = can(HAB.MOBILE_DECLARATION_READ);
  const peutTrancher = can(HAB.SINISTRE_VALIDER_DECLARATION);

  const charger = useCallback(async (page, taille) => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await mobileService.declarations({ page, size: taille });
      setLignes(res.items);
      setPagination((p) => ({ ...p, current: res.page, pageSize: res.pageSize, total: res.total }));
    } catch (err) {
      setErreur(err?.response?.data?.message || t('declarationsMobiles.messages.erreurChargement'));
      setLignes([]);
      setPagination((p) => ({ ...p, total: 0 }));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    if (peutLire) charger(current, pageSize);
    else setChargement(false);
  }, [charger, current, pageSize, peutLire]);

  // Une declaration tranchee quitte la file : on la retire tout de suite plutot que
  // d'attendre le rechargement, sinon elle reste cliquable quelques centaines de ms.
  const apresDecision = (id) => {
    setLignes((prev) => prev.filter((l) => l.sinistreTrackingId !== id));
    setPagination((p) => ({ ...p, total: Math.max(p.total - 1, 0) }));
    setTiroir({ open: false, id: null });
  };

  const enRetard = useMemo(
    () => lignes.filter((l) => (ageEnJours(l.dateDeclaration) ?? 0) >= SEUIL_ALERTE_J).length,
    [lignes],
  );

  const kpis = [
    {
      label: t('declarationsMobiles.kpis.enAttente'),
      value: chargement ? '…' : pagination.total,
      color: MAGENTA,
      icon: <MobileOutlined />,
    },
    {
      label: t('declarationsMobiles.kpis.auDelaDe', { n: SEUIL_ALERTE_J }),
      value: chargement ? '…' : enRetard,
      color: AMBER,
      icon: <ClockCircleOutlined />,
    },
    {
      label: t('declarationsMobiles.kpis.surCettePage'),
      value: chargement ? '…' : lignes.length,
      color: GREEN,
      icon: <EyeOutlined />,
    },
  ];

  const colonnes = [
    {
      title: t('declarationsMobiles.colonnes.declaration'),
      dataIndex: 'numeroSinistreLocal',
      key: 'dossier',
      width: 230,
      render: (v, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontWeight: 800, color: GREEN, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
              {v || t('declarationsMobiles.colonnes.numeroEnAttente')}
            </span>
            {r.typeDommage && (
              <Tag color={DOMMAGE_META[r.typeDommage]?.color || 'default'}
                style={{ borderRadius: 14, fontSize: 10, marginInlineEnd: 0 }}>
                {libelleDommage(r.typeDommage)}
              </Tag>
            )}
          </div>
          <div style={{ fontSize: 12, color: MUTED }}>
            {t('declarationsMobiles.colonnes.accidentDu', { date: r.dateAccident ? formatDate(r.dateAccident).replace(/^Le /, '') : '-' })}
          </div>
        </div>
      ),
    },
    {
      title: t('declarationsMobiles.colonnes.declarant'),
      key: 'declarant',
      width: 250,
      render: (_, r) => {
        const declarant = nomComplet(r.declarantPrenom, r.declarantNom);
        const usager = nomComplet(r.usagerPrenom, r.usagerNom);
        const ecart = declarant && usager && !memeIdentite(r);
        return (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 650, display: 'flex', alignItems: 'center', gap: 6 }}>
              {declarant || <Dash />}
              {ecart && (
                <Tooltip title={t('declarationsMobiles.colonnes.ecartIdentite', { usager })}>
                  <WarningOutlined style={{ color: '#d97706' }} />
                </Tooltip>
              )}
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              {r.declarantTelephone || r.usagerTelephone || <Dash />}
              {r.declarantQualite ? ` · ${r.declarantQualite}` : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: t('declarationsMobiles.colonnes.lieu'),
      key: 'lieu',
      width: 210,
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.lieuAccident || <Dash />}
          </div>
          <div style={{ fontSize: 12, color: MUTED }}>
            {[r.commune, r.ville].filter(Boolean).join(', ') || <Dash />}
          </div>
        </div>
      ),
    },
    {
      title: t('declarationsMobiles.colonnes.joint'),
      key: 'joint',
      width: 110,
      align: 'center',
      render: (_, r) => {
        const gps = r.latitude != null && r.longitude != null;
        // nombreDocuments vaut null quand le comptage n'a pas ete fait : ne jamais
        // l'afficher comme « 0 piece », ce qui orienterait l'agent vers un rejet.
        const nb = r.nombreDocuments;
        return (
          <Space size={8}>
            {gps && (
              <Tooltip title={t('declarationsMobiles.colonnes.positionGps')}>
                <EnvironmentOutlined style={{ color: '#dc2626' }} />
              </Tooltip>
            )}
            {r.memoAudioPresent && (
              <Tooltip title={t('declarationsMobiles.colonnes.memoVocal')}>
                <AudioOutlined style={{ color: '#7c3aed' }} />
              </Tooltip>
            )}
            {nb != null && nb > 0 && (
              <Tooltip title={t('declarationsMobiles.colonnes.piecesAuDossier', { count: nb })}>
                <span style={{ fontSize: 12, color: MUTED }}>
                  <PaperClipOutlined /> {nb}
                </span>
              </Tooltip>
            )}
            {!gps && !r.memoAudioPresent && !nb && <Dash />}
          </Space>
        );
      },
    },
    {
      title: t('declarationsMobiles.colonnes.recue'),
      dataIndex: 'dateDeclaration',
      key: 'recue',
      width: 175,
      render: (v) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 12.5 }}>{v ? formatDate(v) : <Dash />}</span>
          <AgeBadge date={v} />
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <Button
          size="small"
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => setTiroir({ open: true, id: r.sinistreTrackingId })}
          style={{ background: GREEN, borderColor: GREEN }}
        >
          {peutTrancher ? t('declarationsMobiles.colonnes.examiner') : t('declarationsMobiles.colonnes.consulter')}
        </Button>
      ),
    },
  ];

  if (!peutLire) {
    return (
      <Layout title={t('declarationsMobiles.titre')}>
        <Alert
          type="warning"
          showIcon
          message={t('declarationsMobiles.consultationReservee')}
        />
      </Layout>
    );
  }

  return (
    <Layout title={t('declarationsMobiles.titre')}>
      {/* `pagination.total` est le compteur renvoye par le backend pour toute la
          file, pas seulement la page courante - c'est la taille de la file d'attente. */}
      <PageHeader
        titre={t('declarationsMobiles.titre')}
        icone={<MobileOutlined />}
        compte={pagination.total}
        unite={t('declarationsMobiles.unite')}
        actions={(
          <Button icon={<ReloadOutlined />} onClick={() => charger(current, pageSize)}>
            {t('declarationsMobiles.actualiser')}
          </Button>
        )}
      />

      <KpiCards items={kpis} columns={3} />

      {!peutTrancher && (
        <Alert
          type="info"
          showIcon
          message={t('declarationsMobiles.consultationSeule')}
          style={{ marginBottom: 16 }}
        />
      )}

      {erreur && (
        <Alert
          type="error"
          showIcon
          message={erreur}
          action={<Button size="small" onClick={() => charger(current, pageSize)}>{t('declarationsMobiles.reessayer')}</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        className="cb-table"
        rowKey="sinistreTrackingId"
        dataSource={lignes}
        columns={colonnes}
        loading={chargement}
        size="middle"
        scroll={{ x: 1050 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  {t('declarationsMobiles.vide.titre')}
                  <br />
                  <span style={{ fontSize: 12, color: MUTED }}>
                    {t('declarationsMobiles.vide.texte')}
                  </span>
                </span>
              }
            />
          ),
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (total) => t('declarationsMobiles.totalDeclarations', { count: total }),
          onChange: (page, taille) => setPagination((p) => ({ ...p, current: page, pageSize: taille })),
        }}
        onRow={(r) => ({
          onClick: () => setTiroir({ open: true, id: r.sinistreTrackingId }),
          style: { cursor: 'pointer' },
        })}
      />

      <DeclarationMobileDrawer
        open={tiroir.open}
        sinistreTrackingId={tiroir.id}
        onClose={() => setTiroir({ open: false, id: null })}
        onDecision={apresDecision}
      />
    </Layout>
  );
}
